import { computed, ref, type Ref } from 'vue'

import { useRealtimeSubscription, type RealtimeFrame } from '@ja/realtime'

import type { CrudEvent } from './useCrudChannel'

export interface ConflictInfo {
  fields: string[]
  serverData: Record<string, unknown>
  oldData: Record<string, unknown>
  localDirtyFields: string[]
  overlappingFields: string[]
  actorId: string | number | null
  actorName: string | null
  timestamp: string
}

export interface NonConflictingUpdateInfo {
  data: Record<string, unknown>
  oldData: Record<string, unknown>
  actorName: string | null
  actorId: string | number | null
  changedFields: string[]
}

export interface UseEditorRealTimeOptions {
  modelName: string
  recordId: string | number
  currentUserId: string | number | null
  getLocalDirtyFields: () => string[]
  onConflict?: (conflict: ConflictInfo) => void
  onDeleted?: () => void
  onNonConflictingUpdate?: (info: NonConflictingUpdateInfo) => void
}

export interface UseEditorRealTimeReturn {
  isConnected: Ref<boolean>
  hasConflict: Ref<boolean>
  conflictInfo: Ref<ConflictInfo | null>
  dismissConflict: () => void
  acceptServerChanges: () => Record<string, unknown> | null
}

/**
 * Conflict-detection subscription for the Editor page.
 *
 * Subscribes to the per-record `crud:<modelName>:<id>` channel. Reconciles
 * incoming `crud.updated` / `crud.deleted` frames against the user's local
 * dirty-field set.
 */
export function useEditorRealTime(options: UseEditorRealTimeOptions): UseEditorRealTimeReturn {
  const {
    modelName,
    recordId,
    currentUserId,
    getLocalDirtyFields,
    onConflict,
    onDeleted,
    onNonConflictingUpdate,
  } = options

  const hasConflict = ref(false)
  const conflictInfo = ref<ConflictInfo | null>(null)

  const channel = computed(() => `crud:${modelName}:${recordId}`)

  function isOwnEvent(event: CrudEvent): boolean {
    return (
      currentUserId !== undefined &&
      currentUserId !== null &&
      event.actorId === currentUserId
    )
  }

  function handleUpdated(event: CrudEvent): void {
    if (isOwnEvent(event)) return

    const serverChangedFields = event.data ? Object.keys(event.data) : []
    if (serverChangedFields.length === 0) return

    const localDirtyFields = getLocalDirtyFields()
    const overlappingFields = serverChangedFields.filter((f) => localDirtyFields.includes(f))

    if (overlappingFields.length > 0) {
      const conflict: ConflictInfo = {
        fields: serverChangedFields,
        serverData: event.data ?? {},
        oldData: event.oldData ?? {},
        localDirtyFields,
        overlappingFields,
        actorId: event.actorId,
        actorName: event.actorName ?? null,
        timestamp: event.timestamp,
      }
      conflictInfo.value = conflict
      hasConflict.value = true
      onConflict?.(conflict)
    } else if (onNonConflictingUpdate && event.data) {
      onNonConflictingUpdate({
        data: event.data,
        oldData: event.oldData ?? {},
        actorName: event.actorName ?? null,
        actorId: event.actorId,
        changedFields: serverChangedFields,
      })
    }
  }

  const { isConnected } = useRealtimeSubscription<CrudEvent>(channel, {
    onMessage(frame: RealtimeFrame<CrudEvent>) {
      if (frame.event === 'crud.updated') {
        handleUpdated(frame.data as CrudEvent)
      } else if (frame.event === 'crud.deleted') {
        const event = frame.data as CrudEvent
        if (isOwnEvent(event)) return
        onDeleted?.()
      }
    },
  })

  function dismissConflict(): void {
    hasConflict.value = false
    conflictInfo.value = null
  }

  function acceptServerChanges(): Record<string, unknown> | null {
    if (!conflictInfo.value) return null
    const serverData = conflictInfo.value.serverData
    dismissConflict()
    return serverData
  }

  return {
    isConnected,
    hasConflict,
    conflictInfo,
    dismissConflict,
    acceptServerChanges,
  }
}
